import { SeiteHeader } from "../SeiteHeader/SeiteHeader";
import { HeadersOptionsType, StartPageType } from "@/types/ModuleOptionsType";
import { PostHeader } from "../PostHeader/PostHeader";
import { Hero } from "../Hero/Hero";
import { ParentsType } from "../../../wp/getParentPages";
import { EventHeader } from "../EventHeader/EventHeader";
import { Post, PostTypes } from "@/wp/types/client";
import { MagazineHeader } from "../MagazineHeader/MagazineHeader";
import { OptionsFactsheetsType, OptionsHeaderType } from "@/types/OptionType";
import { TeamHeader } from "../TeamHeader/TeamHeader";
import { GlossarHeader } from "../GlossarHeader/GlossarHeader";
import { CatalogHeader } from "../CatalogHeader/CatalogHeader";
import { AVHeader } from "../AVHeader/AVHeader";
import { MetalHeader } from "../MetalHeader/MetalHeader";
import { JobHeader } from "../JobHeader/JobHeader";
import { PressHeader } from "../PressHeader/PressHeader";
import { CategoryOverviewPages } from "@/getData/getIndexedPostGridData";

interface HeaderProps<T extends PostTypes> {
  locale: string;
  post: Post<T>;
  headerOptions: HeadersOptionsType;
  startPageOptions: StartPageType;
  parents: ParentsType;
  options: OptionsHeaderType;
  blogCategories: CategoryOverviewPages[];
  isNobleElements: boolean;
  factsheets: OptionsFactsheetsType;
}

export const Header = <T extends PostTypes>({
  locale,
  post,
  headerOptions,
  startPageOptions,
  parents,
  options,
  blogCategories,
  isNobleElements,
  factsheets,
}: HeaderProps<T>) => {
  // * header_startseite should be always first because there is a bug in the REST API.
  switch (post.type) {
    case "page": {
      if ("header_startseite" in post.acf) {
        return (
          <Hero
            header={post.acf.header_startseite}
            startPageOptions={startPageOptions}
            logos={options.logos}
          />
        );
      } else if ("header_seite" in post.acf) {
        return (
          <SeiteHeader
            post={post}
            headerOptions={headerOptions.header_pages}
            parents={parents}
          />
        );
      }
      break;
    }
    case "catalog": {
      return (
        <CatalogHeader
          post={post}
          headerOptions={headerOptions.header_catalog}
          parents={parents}
        />
      );
    }
    case "blog": {
      return (
        <PostHeader
          post={post}
          headerOptions={headerOptions.header_posts}
          parents={parents}
          fallbackImg={options.fallback_images.header_posts}
          blogCategories={blogCategories}
          isNobleElements={isNobleElements}
        />
      );
    }
    case "events": {
      return (
        <EventHeader
          locale={locale}
          post={post}
          headerOptions={headerOptions.header_events}
          parents={parents}
          fallbackImg={options.fallback_images.header_events}
        />
      );
    }
    case "magazine": {
      return (
        <MagazineHeader
          post={post}
          headerOptions={headerOptions.header_magazine}
          fallbackImg={options.fallback_images.header_magazine}
          parents={parents}
        />
      );
    }
    case "team": {
      return (
        <TeamHeader
          post={post}
          parents={parents}
          fallbackImg={options.fallback_images.header_team}
          headerOptions={headerOptions.header_team}
        />
      );
    }
    case "glossary":
      return (
        <GlossarHeader
          post={post}
          headerOptions={headerOptions.header_glossar}
          parents={parents}
          fallbackImg={options.fallback_images.header_glossar}
        />
      );
    case "videos": {
      return <AVHeader post={post} type={"videos"} />;
    }
    case "audios": {
      return <AVHeader post={post} type={"audios"} />;
    }
    case "metals": {
      return (
        <MetalHeader
          post={post}
          headerOptions={headerOptions?.header_posts}
          parents={parents}
          fallbackImg={options.fallback_images.header_posts}
          factsheets={factsheets}
        />
      );
    }
    case "press": {
      return (
        <PressHeader
          post={post}
          headerOptions={headerOptions.header_posts}
          parents={parents}
          fallbackImg={options.fallback_images.header_press}
        />
      );
    }
    case "jobs":
      return (
        <JobHeader
          post={post}
          parents={parents}
          headerOptions={headerOptions.header_pages}
        />
      );
    case "noble_elements":
      if ("header_startseite" in post.acf) {
        return (
          <Hero
            header={post.acf.header_startseite}
            startPageOptions={startPageOptions}
            logos={options.logos}
            isNobleElements
          />
        );
      } else if ("header_seite" in post.acf) {
        return (
          <SeiteHeader
            post={post}
            headerOptions={headerOptions.header_pages}
            parents={parents}
          />
        );
      }
      break;
    default:
      return null;
  }
};
